import { SortOrder, Worklog, WorklogWhereInput } from '../../../generated/graphql'

type ActiveWorklog = Pick<
  Worklog,
  | 'id'
  | 'end_time'
  | 'deleted_at'
  | 'general_activity'
  | 'specific_activity'
  | 'activity_objective'
  | 'start_time'
>

interface ResolveActiveWorklogParams {
  userId?: string
  trackedWorklogId?: string
  fetchWorklogs: (where: WorklogWhereInput) => Promise<ActiveWorklog[]>
}

export const ACTIVE_WORKLOG_ORDER_BY = [{ created_at: SortOrder.Desc }]

export type TrackedWorklogState =
  | 'untracked'
  | 'pending'
  | 'closed'
  | 'missing'

export interface ResolvedActiveWorklogState {
  activeWorklog?: ActiveWorklog
  trackedWorklog?: ActiveWorklog
  trackedState: TrackedWorklogState
}

export function isPendingWorklog(worklog: ActiveWorklog | null | undefined): worklog is ActiveWorklog {
  return Boolean(worklog && worklog.deleted_at == null && worklog.end_time == null)
}

export async function resolveActiveWorklog({
  userId,
  trackedWorklogId,
  fetchWorklogs,
}: ResolveActiveWorklogParams): Promise<ResolvedActiveWorklogState> {
  if (!userId) {
    return { trackedState: 'untracked' }
  }

  const pending = await fetchWorklogs({
    user_id: { equals: userId },
    deleted_at: { equals: null },
    end_time: { equals: null },
  })

  const activeWorklog = pending.find(isPendingWorklog)
  if (!trackedWorklogId) {
    return {
      activeWorklog,
      trackedState: 'untracked'
    }
  }

  const trackedPendingWorklog = pending.find(worklog => worklog.id === trackedWorklogId)
  if (trackedPendingWorklog) {
    return {
      activeWorklog: trackedPendingWorklog,
      trackedWorklog: trackedPendingWorklog,
      trackedState: 'pending'
    }
  }

  const trackedWorklogs = await fetchWorklogs({
    id: { equals: trackedWorklogId },
    user_id: { equals: userId },
    deleted_at: { equals: null },
  })

  const trackedWorklog = trackedWorklogs[0]
  if (!trackedWorklog) {
    return {
      activeWorklog,
      trackedState: 'missing'
    }
  }

  return {
    activeWorklog,
    trackedWorklog,
    trackedState: trackedWorklog.end_time == null ? 'pending' : 'closed'
  }
}
